import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { PenPortraitInsightVariable, Variable } from "@/types";
import { blackOverlay, toIndex, toPercentage, whiteOverlay } from "@/utils";

interface PenPortraitCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  score: string;
  levels: number[];
  onRemoveSection?: () => void;
  editable: boolean;
}

const PenPortraitCard: React.FC<PenPortraitCardProps> = ({
  title,
  variables,
  score,
  levels,
  onRemoveSection,
  editable
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
      .filter((x) => levels.includes(x.tag_level));
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables, levels]);

  const overlay = useCallback((variable: PenPortraitInsightVariable) => {
    if (variable.tag_type === 'Interests') {
      if (theme.palette.mode === 'dark') {
        return whiteOverlay(0.15)
      } else {
        return blackOverlay(0.15)
      }
    } else {
      return undefined
    }
  }, [theme.palette.mode])

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Card
        key={title}
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "100%",
          containerType: "inline-size",
          position: "relative",
          ":hover": {
            borderColor: editable ? theme.palette.primary.main : undefined,
          },
        }}
      >
        {editable &&
          <HelpBox title="Remove section">
            <IconButton
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 1,
                p: 0.5,
                borderRadius: "6px",
                color: theme.palette.divider,
              }}
              onClick={onRemoveSection}
            >
              <Close fontSize="small" />
            </IconButton>
          </HelpBox>
        }
        <CardHeader
          title={title}
          slotProps={{ title: { variant: "body2" } }}
          sx={{ height: "56px", alignItems: "flex-start", pb: 0 }}
        />
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              gap: { xs: 1, sm: 1.5 },
              '@container (max-width: 420px)': {
                '& .item-5': { display: 'none' },
              },
              '@container (max-width: 360px)': {
                '& .item-4': { display: 'none' },
              },
            }}
          >
            {scores.slice(0, 6).map((variable, index) => (
              <Box
                key={variable.tag_id}
                className={`item-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <HelpBox
                  title={`${variable.tag_name} (${formatFunc(
                    Number(variable[score as keyof PenPortraitInsightVariable],),
                  )})`}
                >
                  <Box
                    component="img"
                    src={variable.image}
                    alt={variable.tag_name}
                    sx={{
                      width: '100%',
                      maxWidth: '64px',
                      height: '100%',
                      mx: 'auto',
                      objectFit: 'contain',
                      transition: 'transform 0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.04)',
                      },
                    }}
                    style={{
                      borderRadius: 5,
                      padding: variable.tag_type === "Interests" ? 4 : undefined,
                      backgroundColor: theme.palette.action.hover,
                      backgroundImage: overlay(variable),
                    }}
                  />
                </HelpBox>
                <Typography
                  variant="caption"
                  sx={{
                    height: "30px",
                    mt: 1,
                    fontSize: "9px",
                    textAlign: "center",
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {variable.tag_name}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }
};

export default PenPortraitCard;
